import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur, fmtDate, statusClass } from "../lib/utils";
import { Check, X, ArrowsClockwise, Calendar } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

export default function AdminRecharges() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/admin/recharge-requests"), api.get("/admin/clients")]);
    setList(r.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/recharge-requests/${id}/${action}`);
      toast.success(action === "approve" ? "Approvato" : "Rifiutato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.company_name || "—";

  return (
    <div className="space-y-6" data-testid="admin-recharges">
      <PageHeader
        eyebrow="Ricariche"
        title="Ricariche manuali"
        subtitle="Richieste di ricarica tramite bonifico bancario inviate dai clienti. Approvale per accreditare il wallet."
        chip="tf-chip-mint"
        icon={ArrowsClockwise}
      />
      <div className="tf-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto scroll-thin">
          <table className="tf-table">
            <thead><tr><th>{t("date")}</th><th>{t("clients")}</th><th>{t("amount")}</th><th>{t("description")}</th><th>{t("status")}</th><th className="text-right">{t("actions")}</th></tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--tf-muted)] py-10">{t("empty")}</td></tr>}
              {list.map((r) => (
                <tr key={r.id} data-testid={`recharge-${r.id}`}>
                  <td className="text-xs font-mono">{fmtDate(r.created_at, lang)}</td>
                  <td>{clientName(r.client_id)}</td>
                  <td className="tf-numeric font-bold">{fmtEur(r.amount)}</td>
                  <td className="text-xs">{r.note || "—"}</td>
                  <td><span className={`tf-status-pill ${statusClass(r.status)}`}>{t(r.status) || r.status}</span></td>
                  <td className="text-right">
                    {r.status === "pending" && (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => act(r.id, "approve")} className="tf-btn tf-btn-primary !px-3 !py-1.5" data-testid={`approve-${r.id}`}>
                          <Check size={14} weight="bold" />{t("approve")}
                        </button>
                        <button onClick={() => act(r.id, "reject")} className="tf-btn tf-btn-danger !px-3 !py-1.5" data-testid={`reject-${r.id}`}>
                          <X size={14} weight="bold" />{t("reject")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden tf-mlist" data-testid="admin-recharges-mlist">
        {list.length === 0 && (
          <div className="tf-card p-8 text-center text-[var(--tf-muted)] text-sm">
            <ArrowsClockwise size={28} weight="duotone" className="mx-auto mb-2 opacity-60" />
            {t("empty")}
          </div>
        )}
        {list.map((r) => (
          <div key={r.id} className="tf-mrow flex-col items-stretch" data-testid={`recharge-card-${r.id}`}>
            <div className="flex items-center gap-3">
              <div className="tf-mrow-icon" style={{ background: "#D1F0E0", color: "#1F6E4A" }}>
                <ArrowsClockwise size={20} weight="fill" />
              </div>
              <div className="tf-mrow-body">
                <div className="tf-mrow-title truncate">{clientName(r.client_id)}</div>
                <div className="tf-mrow-sub flex items-center gap-1.5">
                  <Calendar size={11} weight="bold" />
                  {fmtDate(r.created_at, lang)}
                </div>
                {r.note && <div className="tf-mrow-sub truncate mt-0.5">{r.note}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <div className="font-display font-black text-sm tf-numeric text-[var(--tf-ink)]">{fmtEur(r.amount)}</div>
                <span className={`tf-status-pill ${statusClass(r.status)} text-[9px]`}>{t(r.status) || r.status}</span>
              </div>
            </div>
            {r.status === "pending" && (
              <div className="flex gap-2 pt-3 mt-3 border-t border-[var(--tf-border)]">
                <button
                  onClick={() => act(r.id, "reject")}
                  className="flex-1 rounded-2xl border border-[var(--tf-border)] py-2.5 flex items-center justify-center gap-1.5 text-xs font-bold text-[var(--tf-error)] hover:bg-[#FBDDE1] transition"
                  data-testid={`reject-card-${r.id}`}
                >
                  <X size={13} weight="bold" /> Rifiuta
                </button>
                <button
                  onClick={() => act(r.id, "approve")}
                  className="flex-1 rounded-2xl bg-[var(--tf-primary)] hover:bg-[var(--tf-primary-hover)] py-2.5 flex items-center justify-center gap-1.5 text-xs font-bold text-[var(--tf-ink)] transition"
                  data-testid={`approve-card-${r.id}`}
                >
                  <Check size={13} weight="bold" /> Approva
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
